import React, { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import Scroll from 'react-scroll';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTasks, faTachometer, faEye, faPencil, faArrowLeft, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

import { EQUIPMENT_OPTIONS } from "../../Constants/FieldOptions";
import { putSessionTemplateUpdateAPI } from "../../API/SessionTemplate";
import { getSessionTemplateDetailAPI } from "../../API/SessionTemplate";
import FormErrorBox from "../Reusable/FormErrorBox";
import FormInputField from "../Reusable/FormInputField";
import FormTextareaField from "../Reusable/FormTextareaField";
import FormMultiSelectField from "../Reusable/FormMultiSelectField";
import FormRadioField from "../Reusable/FormRadioField";
// import FormCheckboxField from "../Reusable/FormCheckboxField";
import PageLoadingContent from "../Reusable/PageLoadingContent";
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom'
import { topAlertMessageState, topAlertStatusState } from "../../AppState";


function SessionTemplateUpdate() {
    ////
    //// URL Parameters.
    ////

    const { id } = useParams()

    ////
    //// Global state.
    ////

    const [topAlertMessage, setTopAlertMessage] = useRecoilState(topAlertMessageState);
    const [topAlertStatus, setTopAlertStatus] = useRecoilState(topAlertStatusState);

    ////
    //// Component states.
    ////

    const [errors, setErrors] = useState({});
    const [isFetching, setFetching] = useState(false);
    const [forceURL, setForceURL] = useState("");
    const [created, setCreated] = useState();
    const [userID, setUserID] = useState();
    const [no, setNo] = useState();
    const [completedTime, setCompletedTime] = useState();
    const [duration, setDuration] = useState();
    const [totalDuration, setTotalDuration] = useState();
    const [name, setName] = useState();
    const [description, setDescription] = useState();
    const [brief, setBrief] = useState();
    const [equipment, setEquipment] = useState([]);
    const [isPrimarySession, setIsPrimarySession] = useState(false);
    const [targetHeartRateRatio, setTargetHeartRateRatio] = useState();
    const [sessionType, setSessionType] = useState();
    const [videoSections, setVideoSections] = useState([]);

    ////
    //// Event handling.
    ////

    const onEquipmentChange = (e) => {
        console.log("onEquipmentChange, e:",e);
        let values = [];
        for (let option of e) {
            console.log("option:",option);
            values.push(option.value);
        }
        console.log("onEquipmentChange, values:",values);
        setEquipment(values);
    }

    const onSubmitClick = (e) => {
        console.log("onSubmitClick: Beginning...");
        const sessionTemplate = {
            id: id,
            created: parseInt(created),
            userID: userID,
            no: parseInt(no),
            completedTime: completedTime,
            duration: duration,
            totalDuration: totalDuration,
            name: name,
            description: description,
            brief: brief,
            equipment: equipment,
            isPrimarySession: isPrimarySession,
            targetHeartRateRatio: targetHeartRateRatio,
            sessionType: sessionType,
            videoSections: videoSections,
        };
        console.log("onSubmitClick, sessionTemplate:", sessionTemplate);
        putSessionTemplateUpdateAPI(sessionTemplate, onSessionTemplateUpdateSuccess, onSessionTemplateUpdateError, onSessionTemplateUpdateDone);
    }

    ////
    //// API.
    ////

    function onSessionTemplateDetailSuccess(response){
        console.log("onSessionTemplateDetailSuccess: Starting...");
        setCreated(response.created);
        setUserID(response.userID);
        setNo(response.no);
        setCompletedTime(response.completedTime);
        setDuration(response.duration);
        setTotalDuration(response.totalDuration);
        setName(response.name);
        setDescription(response.description);
        setBrief(response.brief);
        setEquipment(response.equipment);
        setIsPrimarySession(response.isPrimarySession);
        setTargetHeartRateRatio(response.targetHeartRateRatio);
        setSessionType(response.sessionType);
        setVideoSections(response.videoSections);
    }

    function onSessionTemplateDetailError(apiErr) {
        console.log("onSessionTemplateDetailError: Starting...");
        setErrors(apiErr);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onSessionTemplateDetailDone() {
        console.log("onSessionTemplateDetailDone: Starting...");
        setFetching(false);
    }

    function onSessionTemplateUpdateSuccess(response){
        // For debugging purposes only.
        console.log("onSessionTemplateUpdateSuccess: Starting...");
        console.log(response);

        // Add a temporary banner message in the app and then clear itself after 2 seconds.
        setTopAlertMessage("Session template updated");
        setTopAlertStatus("success");
        setTimeout(() => {
            console.log("onSessionTemplateUpdateSuccess: Delayed for 2 seconds.");
            console.log("onSessionTemplateUpdateSuccess: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // Redirect the user to a new page.
        setForceURL("/session-template/"+response.id);
    }

    function onSessionTemplateUpdateError(apiErr) {
        console.log("onSessionTemplateUpdateError: Starting...");
        setErrors(apiErr);

        // Add a temporary banner message in the app and then clear itself after 2 seconds.
        setTopAlertMessage("Failed submitting");
        setTopAlertStatus("danger");
        setTimeout(() => {
            console.log("onSessionTemplateUpdateError: Delayed for 2 seconds.");
            console.log("onSessionTemplateUpdateError: topAlertMessage, topAlertStatus:", topAlertMessage, topAlertStatus);
            setTopAlertMessage("");
        }, 2000);

        // The following code will cause the screen to scroll to the top of
        // the page. Please see ``react-scroll`` for more information:
        // https://github.com/fisshy/react-scroll
        var scroll = Scroll.animateScroll;
        scroll.scrollToTop();
    }

    function onSessionTemplateUpdateDone() {
        console.log("onSessionTemplateUpdateDone: Starting...");
        setFetching(false);
    }

    ////
    //// Misc.
    ////

    useEffect(() => {
        let mounted = true;

        if (mounted) {
            window.scrollTo(0, 0);  // Start the page at the top of the page.
            setFetching(true);
            getSessionTemplateDetailAPI(
                id,
                onSessionTemplateDetailSuccess,
                onSessionTemplateDetailError,
                onSessionTemplateDetailDone
            );
        }

        return () => { mounted = false; }
    }, [id]);

    ////
    //// Component rendering.
    ////

    if (forceURL !== "") {
        return <Navigate to={forceURL}  />
    }

    return (
        <>
            <div class="container">
                <section class="section">
                    <nav class="breadcrumb" aria-label="breadcrumbs">
                        <ul>
                            <li><Link to="/dashboard"><FontAwesomeIcon className="fas" icon={faTachometer} />&nbsp;Dashboard</Link></li>
                            <li class=""><Link to="/session-templates" aria-current="page"><FontAwesomeIcon className="fas" icon={faTasks} />&nbsp;Session Templates</Link></li>
                            <li class=""><Link to={`/session-template/${id}`} aria-current="page"><FontAwesomeIcon className="fas" icon={faEye} />&nbsp;Details</Link></li>
                            <li class="is-active"><Link to={`/session-template/${id}/edit`} aria-current="page"><FontAwesomeIcon className="fas" icon={faPencil} />&nbsp;Edit</Link></li>
                        </ul>
                    </nav>
                    <nav class="box">
                        <p class="title is-4"><FontAwesomeIcon className="fas" icon={faTasks} />&nbsp;Session Template</p>
                        <FormErrorBox errors={errors} />

                        <p class="subtitle is-6">Details</p>

                        {isFetching
                            ?
                            <PageLoadingContent displayMessage={"Please wait"} />
                            :
                            <>
                                <div class="container">
                                    <FormInputField
                                        label="ID"
                                        name="id"
                                        placeholder="Text input"
                                        value={id}
                                        errorText={errors && errors.id}
                                        helpText=""
                                        onChange={null}
                                        isRequired={true}
                                        maxWidth="200px"
                                        disabled={true}
                                    />

                                    <FormInputField
                                        label="Name"
                                        name="name"
                                        placeholder="Text input"
                                        value={name}
                                        errorText={errors && errors.name}
                                        helpText=""
                                        onChange={(e)=>setName(e.target.value)}
                                        isRequired={true}
                                        maxWidth="350px"
                                    />

                                    <FormTextareaField
                                        label="Description (Optional)"
                                        name="description"
                                        placeholder="Text input"
                                        value={description}
                                        errorText={errors && errors.description}
                                        helpText=""
                                        onChange={(e)=>setDescription(e.target.value)}
                                        isRequired={true}
                                        maxWidth="100%"
                                        rows={4}
                                    />

                                    <FormTextareaField
                                        label="Brief (Optional)"
                                        name="brief"
                                        placeholder="Text input"
                                        value={brief}
                                        errorText={errors && errors.brief}
                                        helpText=""
                                        onChange={(e)=>setBrief(e.target.value)}
                                        isRequired={true}
                                        maxWidth="100%"
                                        rows={2}
                                    />

                                    <FormInputField
                                        label="Duration (Optional)"
                                        name="duration"
                                        type="number"
                                        placeholder="#"
                                        value={duration}
                                        errorText={errors && errors.duration}
                                        helpText="Measured in minutes"
                                        onChange={(e)=>setDuration(parseInt(e.target.value))}
                                        isRequired={true}
                                        maxWidth="100px"
                                    />

                                    <FormMultiSelectField
                                        label="Equipment (Optional)"
                                        name="equipment"
                                        placeholder="Text input"
                                        options={EQUIPMENT_OPTIONS}
                                        selectedValues={equipment}
                                        onChange={onEquipmentChange}
                                        errorText={errors && errors.equipment}
                                        helpText="Pick the equipment associated with this session template."
                                        isRequired={true}
                                        maxWidth="320px"
                                    />

                                    <FormInputField
                                        label="Target Heart Rate Ratio (Optional)"
                                        name="targetHeartRateRatio"
                                        type="number"
                                        placeholder="#"
                                        value={targetHeartRateRatio}
                                        errorText={errors && errors.targetHeartRateRatio}
                                        helpText=""
                                        onChange={(e)=>setTargetHeartRateRatio(parseInt(e.target.value))}
                                        isRequired={true}
                                        maxWidth="75px"
                                    />

                                    <FormRadioField
                                        label="Session Type"
                                        name="sessionType"
                                        value={sessionType}
                                        opt1Value="base"
                                        opt1Label="Base"
                                        opt2Value="breath"
                                        opt2Label="Breath"
                                        opt3Value="sleep"
                                        opt3Label="Sleep"
                                        errorText={errors && errors.sessionType}
                                        onChange={(e)=>setSessionType(e.target.value)}
                                        maxWidth="180px"
                                    />

                                    {/* <FormCheckboxField
                                        label="Is primary session?"
                                        name="isPrimarySession"
                                        checked={isPrimarySession}
                                        errorText={errors && errors.isPrimarySession}
                                        wasValidated={false}
                                        helpText=""
                                        onChange={(e)=>setIsPrimarySession(!isPrimarySession)}
                                    /> */}

                                    <div class="columns">
                                        <div class="column is-half">
                                            <Link to={`/session-template/${id}`} class="button is-fullwidth-mobile"><FontAwesomeIcon className="fas" icon={faArrowLeft} />&nbsp;Back to Detail</Link>
                                        </div>
                                        <div class="column is-half has-text-right">
                                            <button class="button is-primary is-fullwidth-mobile" onClick={onSubmitClick}><FontAwesomeIcon className="fas" icon={faCheckCircle} />&nbsp;Save</button>
                                        </div>
                                    </div>

                                </div>
                            </>
                        }
                    </nav>
                </section>
            </div>
        </>
    );
}

export default SessionTemplateUpdate;
